/**
 * Veloza Membership — the single client-side membership implementation.
 *
 *   Membership Price  ([data-veloza-member], rendered by snippets/buy-buttons.liquid)
 *     → adds Veloza Membership (only if it is not in the cart yet) + the selected
 *       variant in ONE /cart/add.js request, then redirects to the cart.
 *   Guest Price       (the theme's normal submit button)
 *     → handled by Dawn's <product-form>; adds only the selected variant.
 *   Membership product page (<product-form data-veloza-membership-form>)
 *     → adds the membership only if it is not in the cart yet, then opens the cart.
 *   Join & Save       ([data-veloza-join], VIP Club page sections)
 *     → same as the membership product page.
 *
 * Recurring billing is not handled here; the checkout/payment provider runs the monthly renewals.
 * Loaded from buy-buttons.liquid and the VIP Club sections; the guard below binds listeners once.
 */
(function () {
  if (window.VelozaMembership) return;

  const routes = window.routes || {};
  const cartUrl = routes.cart_url || '/cart';
  const cartAddUrl = routes.cart_add_url || '/cart/add';

  async function getCart() {
    return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  async function addItems(items) {
    const response = await fetch(`${cartAddUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Could not add to cart.');
    }
  }

  function getProductForm(button) {
    return button.closest('product-form');
  }

  function showError(productForm, message) {
    const wrapper = productForm && productForm.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) {
      if (message) window.alert(message);
      return;
    }
    wrapper.querySelector('.product-form__error-message').textContent = message;
    wrapper.toggleAttribute('hidden', !message);
  }

  function setLoading(button, loading) {
    button.classList.toggle('loading', loading);
    if (loading) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }

  async function addWithMembership(button) {
    const form = document.getElementById(button.dataset.formId);
    const guestButton = form && form.querySelector('[data-veloza-guest]');
    if (!form || button.getAttribute('aria-disabled') === 'true') return;
    if (button.disabled || (guestButton && guestButton.disabled)) return;

    // FormData also picks up the quantity input, which sits outside the form (form="…").
    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const membershipVariantId = Number(button.dataset.membershipVariantId);
    const productForm = getProductForm(button);

    if (!variantId) {
      showError(productForm, (window.variantStrings && window.variantStrings.unavailable) || 'Please choose an option.');
      return;
    }

    showError(productForm, '');
    setLoading(button, true);

    try {
      const cart = await getCart();
      const hasMembership = (cart.items || []).some((item) => item.variant_id === membershipVariantId);

      const items = [];
      if (!hasMembership) items.push({ id: membershipVariantId, quantity: 1 });
      items.push({ id: variantId, quantity });
      await addItems(items);

      window.location.href = cartUrl;
    } catch (error) {
      setLoading(button, false);
      showError(productForm, error.message);
    }
  }

  // Membership only (product page "Join now" and VIP Club "Join & Save").
  async function joinMembership(variantId) {
    const id = Number(variantId);
    if (!id) throw new Error('The VIP Membership is not available right now.');
    const cart = await getCart();
    const inCart = (cart.items || []).some((item) => item.variant_id === id);
    if (!inCart) await addItems([{ id, quantity: 1 }]);
    window.location.href = cartUrl;
  }

  // Keep the price boxes and Membership Price / Guest Price buttons in sync when the customer picks
  // another variant. product-info.js re-renders the section and passes the new HTML here; Dawn itself
  // would overwrite the Guest label with "Add to cart".
  function syncButtons({ data }) {
    if (!data || !data.html) return;
    document.querySelectorAll('product-info').forEach((productInfo) => {
      const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
      if (sectionId !== data.sectionId) return;

      ['.veloza-price-compare', '[data-veloza-member]', '[data-veloza-guest]'].forEach((selector) => {
        const source = data.html.querySelector(selector);
        const destination = productInfo.querySelector(selector);
        if (!source || !destination) return;
        destination.innerHTML = source.innerHTML;
        destination.toggleAttribute('disabled', source.hasAttribute('disabled'));
      });
    });
  }

  document.addEventListener('click', (event) => {
    const memberButton = event.target.closest('[data-veloza-member]');
    if (memberButton) {
      event.preventDefault();
      addWithMembership(memberButton);
      return;
    }

    const joinButton = event.target.closest('[data-veloza-join]');
    if (!joinButton) return;
    event.preventDefault();
    if (joinButton.getAttribute('aria-disabled') === 'true') return;
    const label = joinButton.innerHTML;
    const errorBox = joinButton.parentElement && joinButton.parentElement.querySelector('[data-veloza-join-error]');
    if (errorBox) errorBox.textContent = '';
    joinButton.setAttribute('aria-disabled', 'true');
    joinButton.textContent = 'Adding…';
    joinMembership(joinButton.dataset.variantId).catch((error) => {
      joinButton.removeAttribute('aria-disabled');
      joinButton.innerHTML = label;
      if (errorBox) errorBox.textContent = error.message;
      else window.alert(error.message);
    });
  });

  // "Add Veloza Membership" checkbox (snippets/membership-optin.liquid) keeps the cart in sync:
  // checking adds one VIP Membership line marked _vz_source=checkbox (only if no membership line exists);
  // unchecking removes only lines this checkbox added. A membership added elsewhere is never removed.
  // Removing the regular product does not remove the membership; the cart summary shows it and how to remove it.
  const OPTIN_SOURCE = 'checkbox';
  const cartChangeUrl = routes.cart_change_url || '/cart/change';

  function membershipLines(cart, variantId) {
    return (cart.items || []).filter((item) => item.variant_id === variantId);
  }

  function membershipIds(box) {
    return [Number(box.dataset.membershipVariantId), Number(box.dataset.fullMembershipVariantId)].filter(Boolean);
  }

  async function setLineQuantity(key, quantity) {
    const response = await fetch(`${cartChangeUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) throw new Error('Could not update the cart.');
  }

  function isOptinLine(item) {
    return Boolean(item.properties && item.properties._vz_source === OPTIN_SOURCE);
  }

  function renderOptin(box, cart) {
    const lines = membershipLines(cart, Number(box.dataset.membershipVariantId));
    const fullLines = membershipLines(cart, Number(box.dataset.fullMembershipVariantId));
    const addedElsewhere = fullLines.length > 0 || lines.some((line) => !isOptinLine(line));
    box.checked = lines.length > 0 || fullLines.length > 0;
    box.disabled = addedElsewhere;
    const status = box.closest('.vz-optin') && box.closest('.vz-optin').querySelector('[data-vz-optin-status]');
    if (!status) return;
    if (addedElsewhere) status.textContent = 'The Veloza Membership is already in your cart.';
    else if (lines.length) status.textContent = 'Veloza Membership added to your cart.';
    else status.textContent = '';
  }

  async function syncOptins() {
    const boxes = document.querySelectorAll('[data-vz-optin]');
    if (!boxes.length) return;
    let cart = await getCart();
    // A membership is one per cart: put any membership line whose quantity was raised back to 1.
    const ids = membershipIds(boxes[0]);
    const extra = (cart.items || []).filter((item) => ids.includes(item.variant_id) && item.quantity > 1);
    if (extra.length) {
      for (const line of extra) await setLineQuantity(line.key, 1);
      cart = await getCart();
    }
    boxes.forEach((box) => renderOptin(box, cart));
  }

  document.addEventListener('change', async (event) => {
    const box = event.target.closest('[data-vz-optin]');
    if (!box) return;
    const variantId = Number(box.dataset.membershipVariantId);
    const wanted = box.checked;
    box.disabled = true;
    try {
      const cart = await getCart();
      const lines = membershipLines(cart, variantId);
      const fullLines = membershipLines(cart, Number(box.dataset.fullMembershipVariantId));
      if (wanted && lines.length === 0 && fullLines.length === 0) {
        await addItems([{ id: variantId, quantity: 1, properties: { _vz_source: OPTIN_SOURCE } }]);
      } else if (!wanted) {
        for (const line of lines.filter(isOptinLine)) await setLineQuantity(line.key, 0);
      }
      await syncOptins();
    } catch (error) {
      box.checked = !wanted;
      box.disabled = false;
      window.alert(error.message);
    }
  });

  // Re-sync after page load, cart changes and when a quick-add modal injects a new checkbox.
  let optinSyncTimer;
  function scheduleOptinSync() {
    clearTimeout(optinSyncTimer);
    optinSyncTimer = setTimeout(() => syncOptins().catch(() => {}), 150);
  }
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (node.matches('[data-vz-optin]') || node.querySelector('[data-vz-optin]'))) {
          scheduleOptinSync();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Membership product page: route the submit through joinMembership (no second membership line).
  document.addEventListener(
    'submit',
    (event) => {
      const productForm = event.target.closest('product-form[data-veloza-membership-form]');
      if (!productForm) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const button = productForm.querySelector('[type="submit"]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      showError(productForm, '');
      setLoading(button, true);
      joinMembership(productForm.dataset.membershipVariantId).catch((error) => {
        setLoading(button, false);
        showError(productForm, error.message);
      });
    },
    true
  );

  function subscribeToVariantChanges() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncButtons);
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleOptinSync);
    }
    scheduleOptinSync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribeToVariantChanges);
  else subscribeToVariantChanges();

  window.VelozaMembership = { addWithMembership, joinMembership };
})();
